import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteAPI, miscAPI } from '../../api';
import { toast } from 'sonner';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    siteAPI.getSettings().then(res => setCms(res.data)).catch(() => {});
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await miscAPI.subscribeNewsletter(email);
      setSubscribed(true);
      toast.success('Subscribed!');
    } catch { toast.error('Subscription failed'); }
  };

  const nl = cms?.newsletter || { title: 'SUBSCRIBE TO OUR NEWSLETTER', subtitle: 'Be the first to discover new arrivals and insider news.', background_image: '' };
  const ft = cms?.footer || { menus: [], social_links: {}, copyright_text: 'Petals West Inc. All rights reserved.' };
  const social = ft.social_links || {};

  return (
    <>
      {/* Newsletter Section - Split layout: tulips left, form right */}
      <section aria-labelledby="newsletter-title" data-testid="home-newsletter" className="w-full grid md:grid-cols-2" style={{ minHeight: '400px', marginTop: '40px' }}>
        {/* Left - Tulips image from mockup, showing only floral area */}
        <div
          className="relative overflow-hidden hidden md:block"
          style={{
            backgroundImage: `url(${nl.background_image})`,
            backgroundSize: '280% auto',
            backgroundPosition: '5% center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: '#f0efed',
          }}
        />
        {/* Right - Newsletter form on clean grey background */}
        <div className="bg-[#f0efed] flex flex-col justify-center px-8 md:px-16 py-12 md:py-16">
          <h2 id="newsletter-title" className="text-lg md:text-xl font-bold tracking-[0.12em] uppercase mb-4 text-[#2d2d2d]">{nl.title}</h2>
          <p className="text-sm text-[#4a4a4a] mb-8">{(nl.subtitle || '').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < (nl.subtitle || '').split('\n').length - 1 && <br />}</React.Fragment>)}</p>
          {subscribed ? (
            <p className="text-[#2f6b4f] font-medium">Thank you for subscribing!</p>
          ) : (
            <form onSubmit={handleNewsletter} className="space-y-5">
              <div>
                <label htmlFor="newsletter-email" className="text-sm text-[#4a4a4a] block mb-2">Email *</label>
                <input
                  id="newsletter-email"
                  data-testid="newsletter-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-0 py-3 border-0 border-b border-[#c0c0c0] text-sm focus:outline-none focus:border-[#2d2d2d] bg-transparent"
                />
              </div>
              <div className="flex items-center justify-between gap-4 pt-2">
                <label className="flex items-center gap-2 text-xs text-[#6f6a63] cursor-pointer">
                  <input type="checkbox" required className="w-4 h-4 border-[#c0c0c0] rounded" />
                  Yes, subscribe me to your newsletter. *
                </label>
                <button data-testid="newsletter-subscribe-button" type="submit" className="bg-[#2f3e36] text-white px-8 py-3 text-xs tracking-[0.18em] uppercase rounded-full hover:bg-[#253029] transition-colors flex-shrink-0">
                  SUBSCRIBE
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-white border-t border-[#e7e3dd]">
        <div className="pw-container py-10 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {ft.menus.map((menu, i) => (
              <div key={i}>
                <h3 className="text-xs font-semibold tracking-[0.15em] uppercase mb-4">{menu.title}</h3>
                <ul className="space-y-2">
                  {(menu.links || []).map((link, j) => (
                    <li key={j}>
                      {link.url ? (
                        link.url.startsWith('http') || link.url.startsWith('mailto:') ? (
                          <a href={link.url} target={link.url.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="text-xs text-[#6f6a63] hover:text-[#2d2d2d] transition-colors">
                            {link.text}
                          </a>
                        ) : (
                          <Link to={link.url} className="text-xs text-[#6f6a63] hover:text-[#2d2d2d] transition-colors">
                            {link.text}
                          </Link>
                        )
                      ) : (
                        <span className="text-xs text-[#6f6a63]">{link.text}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-[#e7e3dd] flex items-start justify-between">
            {/* Social */}
            <div className="flex gap-4 items-center">
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Petals West on Instagram" data-testid="footer-instagram-link" className="text-[#6f6a63] hover:text-[#2d2d2d]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                </a>
              )}
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Petals West on Facebook" data-testid="footer-facebook-link" className="text-[#6f6a63] hover:text-[#2d2d2d]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                </a>
              )}
              {social.pinterest && (
                <a href={social.pinterest} target="_blank" rel="noopener noreferrer" aria-label="Petals West on Pinterest" className="text-[#6f6a63] hover:text-[#2d2d2d]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.237 2.636 7.855 6.356 9.312-.088-.791-.167-2.005.035-2.868.182-.78 1.172-4.97 1.172-4.97s-.299-.598-.299-1.482c0-1.388.806-2.425 1.808-2.425.853 0 1.265.64 1.265 1.408 0 .858-.546 2.14-.828 3.33-.236.995.5 1.807 1.48 1.807 1.778 0 3.144-1.874 3.144-4.58 0-2.393-1.72-4.068-4.177-4.068-2.845 0-4.515 2.135-4.515 4.34 0 .859.331 1.781.745 2.283a.3.3 0 0 1 .069.288l-.278 1.133c-.044.183-.145.222-.335.134-1.249-.581-2.03-2.407-2.03-3.874 0-3.154 2.292-6.052 6.608-6.052 3.469 0 6.165 2.473 6.165 5.776 0 3.447-2.173 6.22-5.19 6.22-1.013 0-1.965-.527-2.291-1.148l-.623 2.378c-.226.869-.835 1.958-1.244 2.621.937.29 1.931.446 2.962.446 5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
                </a>
              )}
            </div>

            {/* Copyright */}
            <div>
              <div className="text-right text-xs text-[#6f6a63]">
                &copy; {new Date().getFullYear()} {ft.copyright_text}
              </div>
              <div className="text-right mt-2 flex items-center justify-end gap-1.5">
                <img src="https://customer-assets.emergentagent.com/job_291951d3-4c04-4e5d-b3ee-9e43e83189ef/artifacts/92m1dw8e_image.png" alt="Emprenovus" className="inline-block h-4 w-4 rounded-full" />
                <span className="text-[11px] text-[#6f6a63]">AI Powered by</span>
                <a href="https://www.emprenovus.com/" target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#6f6a63] font-medium hover:text-[#2d2d2d] transition-colors underline underline-offset-2">Emprenovus</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
